import { api } from '../services/axiosConfig';
import { MapConnection, MapFolder, MapSystem, ShareScope, TrackedConnection } from '../types';

export const fetchMaps = () => api.get<MapFolder[]>('/api/maps').then((r) => r.data);

export const createMap = (body: { name: string; shareScope: ShareScope; shareGroupId?: number; description?: string }) =>
  api.post<MapFolder>('/api/maps', body).then((r) => r.data);

export const updateMap = (id: number, body: Partial<{ name: string; shareScope: ShareScope; shareGroupId: number; description: string; autoTrack: boolean }>) =>
  api.patch<MapFolder>(`/api/maps/${id}`, body).then((r) => r.data);

export const deleteMap = (id: number) => api.delete(`/api/maps/${id}`);

export const fetchMapSystems = (mapId: number) => api.get<MapSystem[]>(`/api/maps/${mapId}/systems`).then((r) => r.data);

export const addMapSystem = (
  mapId: number,
  body: { solarSystemId: number; label?: string; tag?: string; isHome?: boolean },
) =>
  api.post<MapSystem>(`/api/maps/${mapId}/systems`, body).then((r) => r.data);

export const updateMapSystem = (
  mapId: number,
  systemId: number,
  body: Partial<{
    label: string;
    notes: string;
    tag: string;
    isPinned: boolean;
    isHome: boolean;
    /** Clears an auto-tracked entry's expiry — "we're keeping this one". */
    keep: boolean;
    notesVisibility: 'shared' | 'private';
  }>,
) => api.patch<MapSystem>(`/api/maps/${mapId}/systems/${systemId}`, body).then((r) => r.data);

/** Renames a compartment across every entry filed under it — one server-side
 * UPDATE, since re-tagging entry by entry forks the drawer on the first
 * failure. Pass the CURRENT tag in the path ('' for Uncategorized). */
export const renameCompartment = (mapId: number, current: string, name: string) =>
  api
    .patch<{ renamed: number }>(`/api/maps/${mapId}/compartments/${encodeURIComponent(current)}`, { name })
    .then((r) => r.data);

/** The TRACKED compartment: J->J hops the presence poller recorded. Read-only
 * — promotion into the curated list goes through addMapConnection. */
export const fetchTrackedConnections = (mapId: number, hours = 48) =>
  api.get<TrackedConnection[]>(`/api/maps/${mapId}/tracked`, { params: { hours } }).then((r) => r.data);

export const removeMapSystem = (mapId: number, systemId: number) => api.delete(`/api/maps/${mapId}/systems/${systemId}`);

export const fetchMapConnections = (mapId: number) =>
  api.get<MapConnection[]>(`/api/maps/${mapId}/connections`).then((r) => r.data);

export const addMapConnection = (
  mapId: number,
  body: {
    fromMapSystemId: number;
    toMapSystemId?: number;
    signatureCode?: string;
    whTypeCode?: string;
    massStatus?: string;
    lifeStatus?: string;
    notes?: string;
  },
) => api.post<MapConnection>(`/api/maps/${mapId}/connections`, body).then((r) => r.data);

export const updateMapConnection = (
  mapId: number,
  connectionId: number,
  body: Partial<{
    toMapSystemId: number;
    signatureCode: string;
    whTypeCode: string;
    massStatus: string;
    lifeStatus: string;
    notes: string;
    collapsed: boolean;
  }>,
) => api.patch<MapConnection>(`/api/maps/${mapId}/connections/${connectionId}`, body).then((r) => r.data);

export const removeMapConnection = (mapId: number, connectionId: number) =>
  api.delete(`/api/maps/${mapId}/connections/${connectionId}`);

